// AspectLayer - Visualizes slope direction (aspect) from DEM tiles
// Inherits common tile management logic from TerrainLayer
// Specific to aspect: fixed palette and aspect color computation

import { calculateAspect, calculateSobel, decodeDEM } from '../../utils/terrainCalculations';
import TerrainLayer from './TerrainLayer';

class AspectLayer extends TerrainLayer {
    /**
     * Get path to aspect worker script
     */
    getWorkerPath () {
        return '/static/workers/aspectWorker.js';
    }

    /**
     * Initialize aspect layer
     * @param {string} accessToken - Mapbox access token
     * @param {mapboxgl.Map} map - Mapbox map instance
     * @param {string} palette - Color palette name (default '8direction')
     */
    constructor (accessToken, map, palette = '8direction') {
        super(accessToken, map, { layerNamePrefix: 'aspect' });
        this.palette = palette;
    }

    /**
     * Post message to worker with palette parameter
     */
    postMessageToWorker (worker, imageData, z, x, y) {
        worker.postMessage({ imageData, z, x, y, palette: this.palette });
    }

    /**
     * Process tile blob - decode DEM and compute aspect colors
     */
    async processTile (blob, z, x, y) {
        const blobUrl = URL.createObjectURL(blob);
        try {
            const img = await this.loadImage(blobUrl);
            return await this.processImage(img, z, x, y);
        } finally {
            URL.revokeObjectURL(blobUrl);
        }
    }

    /**
     * Fallback: Process image on main thread (if worker fails)
     * Computes aspect from DEM using Sobel gradients
     */
    processImageMainThread = (img, z, x, y) => {
        const tileSize = 512;
        const canvas = document.createElement('canvas');
        canvas.width = tileSize;
        canvas.height = tileSize;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, tileSize, tileSize);
        const data = imageData.data;
        const dem = decodeDEM(data);

        // Process each pixel
        for (let pixelIdx = 0; pixelIdx < dem.length; pixelIdx++) {
            const elevation = dem[pixelIdx];
            if (elevation <= 0) {
                const dataIdx = pixelIdx * 4;
                data[dataIdx] = 0;
                data[dataIdx + 1] = 0;
                data[dataIdx + 2] = 0;
                data[dataIdx + 3] = 0;
                continue;
            }
            const row = Math.floor(pixelIdx / tileSize);
            const col = pixelIdx % tileSize;
            const { gx, gy } = calculateSobel(dem, tileSize, tileSize, col, row);
            const aspect = calculateAspect(gx, gy);
            const color = this.getAspectColor(aspect);
            const dataIdx = pixelIdx * 4;
            data[dataIdx] = color.r;
            data[dataIdx + 1] = color.g;
            data[dataIdx + 2] = color.b;
            data[dataIdx + 3] = 255;
        }

        ctx.putImageData(imageData, 0, 0);
        return canvas;
    };

    /**
     * Get color for aspect angle (0-360 degrees)
     * 8-direction palette matching aspectWorker.js
     */
    getAspectColor = aspect => {
        const directions = [
            { start: 337.5, end: 22.5, r: 132, g: 214, b: 0 },       // N - Green
            { start: 22.5, end: 67.5, r: 0, g: 171, b: 68 },         // NE - Green
            { start: 67.5, end: 112.5, r: 0, g: 104, b: 192 },       // E - Blue
            { start: 112.5, end: 157.5, r: 108, g: 0, b: 163 },      // SE - Purple
            { start: 157.5, end: 202.5, r: 202, g: 0, b: 156 },      // S - Magenta
            { start: 202.5, end: 247.5, r: 255, g: 85, b: 104 },     // SW - Red
            { start: 247.5, end: 292.5, r: 255, g: 171, b: 71 },     // W - Orange
            { start: 292.5, end: 337.5, r: 244, g: 250, b: 0 },      // NW - Yellow
        ];
        for (const dir of directions) {
            let inRange = false;
            if (dir.start > dir.end) {
                inRange = aspect >= dir.start || aspect < dir.end;
            } else {
                inRange = aspect >= dir.start && aspect < dir.end;
            }
            if (inRange) {
                return { r: dir.r, g: dir.g, b: dir.b };
            }
        }
        return { r: 0, g: 0, b: 0 };
    };
}

export default AspectLayer;
